/**
 * Attachments (Phase 5) — receipts, invoices, and other supporting
 * documents, stored in APPLICATION-MANAGED storage (spec Section 9):
 * files are COPIED into the app-data root, never referenced in place, and
 * the database stores an app-relative path — never a machine-specific
 * absolute path — for portability.
 *
 * Association tables (purchase_attachments, actual_expense_attachments,
 * …) give each link a real foreign key; no polymorphic target_type/
 * target_id. One stored file may be linked from multiple records; SHA-256
 * detects duplicates so the same bytes are never stored twice.
 *
 * A missing managed file is a visible "File Missing" state — the record
 * is never silently removed.
 */

export type AttachmentKind = "receipt" | "invoice" | "delivery" | "return-receipt" | "other";

export const ATTACHMENT_KINDS: ReadonlyArray<{ id: AttachmentKind; displayName: string; folder: string }> = [
  { id: "receipt", displayName: "Receipt", folder: "receipts" },
  { id: "invoice", displayName: "Invoice", folder: "invoices" },
  { id: "delivery", displayName: "Delivery ticket", folder: "other" },
  { id: "return-receipt", displayName: "Return receipt", folder: "receipts" },
  { id: "other", displayName: "Other", folder: "other" },
];

export interface Attachment {
  id: string;
  kind: AttachmentKind;
  originalFilename: string;
  /** Relative to the app-data root, forward slashes, e.g. attachments/receipts/2026/08/<uuid>.jpg */
  storedRelativePath: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  importedAt: string;
  archivedAt: string | null;
}

export function isImageMime(mime: string): boolean {
  return /^image\/(png|jpe?g|gif|webp|bmp)$/i.test(mime);
}

export function isPdfMime(mime: string): boolean {
  return mime.toLowerCase() === "application/pdf";
}

/** Best-effort MIME from filename extension. */
export function mimeFromFilename(filename: string): string {
  const ext = filename.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "bmp":
      return "image/bmp";
    case "pdf":
      return "application/pdf";
    case "txt":
      return "text/plain";
    case "csv":
      return "text/csv";
    default:
      return "application/octet-stream";
  }
}

/** Build the managed relative path for a new attachment. */
export function managedRelativePath(
  kind: AttachmentKind,
  id: string,
  originalFilename: string,
  importedAt: Date,
): string {
  const folder = ATTACHMENT_KINDS.find((k) => k.id === kind)?.folder ?? "other";
  const ext = originalFilename.includes(".") ? originalFilename.split(".").pop()!.toLowerCase() : "";
  const yyyy = importedAt.getUTCFullYear().toString();
  const mm = (importedAt.getUTCMonth() + 1).toString().padStart(2, "0");
  return `attachments/${folder}/${yyyy}/${mm}/${id}${ext ? "." + ext : ""}`;
}
